import React, { useMemo } from 'react';
import { isBefore, isAfter, isSameMinute } from 'date-fns';
import { MessageSquareText, CircleCheck, Smile, Ticket, HelpCircle } from 'lucide-react';

import { parseUTCDate, formatDateInTimezone, calculateScheduleTime } from 'utils/dateTimeUtils';
import { PRETTY_DATE_TIME_TZ_FORMAT } from 'constants/dates';
import { EditableEvent, EditableSession } from 'types/event.types';
import { TimeOffsetUnit, REMINDER_STATUS } from 'constants/enums';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { convertSendAtToDBValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';

import { ScheduleImpactCardProps } from '../ScheduleImpactCard/ScheduleImpactCard';

type ValidationResult = (ScheduleImpactCardProps | null)[];

interface EventTimes {
  startDateTime: Date;
  endDateTime: Date;
  timezoneCode: string;
}

interface BorderSessionsResult {
  startSession: EditableSession | null;
  endSession: EditableSession | null;
}

/**
 * Calculate border sessions from a list of sessions
 * Returns the sessions with earliest start and latest end times
 */
const getBorderSessions = (sessions: EditableSession[]): BorderSessionsResult => {
  if (!sessions || sessions.length === 0) {
    return {
      startSession: null,
      endSession: null
    };
  }

  return sessions.reduce(
    (acc, current) => {
      const currentStart = parseUTCDate(current.datetimeScheduled!);
      const currentEnd = parseUTCDate(current.datetimeEnd!);

      let sessionWithEarliestStart = acc.startSession;
      if (!sessionWithEarliestStart) {
        sessionWithEarliestStart = current;
      } else {
        const earliestStart = parseUTCDate(sessionWithEarliestStart.datetimeScheduled!);
        if (currentStart.getTime() < earliestStart.getTime()) {
          sessionWithEarliestStart = current;
        }
      }

      let sessionWithLatestEnd = acc.endSession;
      if (!sessionWithLatestEnd) {
        sessionWithLatestEnd = current;
      } else {
        const latestEnd = parseUTCDate(sessionWithLatestEnd.datetimeEnd!);
        if (currentEnd.getTime() > latestEnd.getTime()) {
          sessionWithLatestEnd = current;
        }
      }

      return {
        startSession: sessionWithEarliestStart,
        endSession: sessionWithLatestEnd
      };
    },
    {
      startSession: null as EditableSession | null,
      endSession: null as EditableSession | null
    }
  );
};

/**
 * Get a human-readable relative time description
 */
const getRelativeTimeDescription = (
  quantity: number,
  unit: string,
  beforeEventBegins: boolean | null = null,
  beforeEventEnds: boolean | null = null
): string => {
  const unitMap: Record<string, string> = {
    [TimeOffsetUnit.MINUTES]: quantity === 1 ? 'minute' : 'minutes',
    [TimeOffsetUnit.HOURS]: quantity === 1 ? 'hour' : 'hours',
    [TimeOffsetUnit.DAYS]: quantity === 1 ? 'day' : 'days'
  };

  const unitName = unitMap[unit.toLowerCase()] || unit;

  let timing: string;
  if (beforeEventBegins !== null) {
    timing = beforeEventBegins ? 'before event starts' : 'after event starts';
  } else if (beforeEventEnds !== null) {
    timing = beforeEventEnds ? 'before event ends' : 'after event ends';
  } else {
    timing = '';
  }

  return `${quantity} ${unitName} ${timing}`;
};

/**
 * Validate Slack reminders
 * Returns an array of validation cards for scheduled Slack reminders
 */
const validateSlackReminders = (
  event: EditableEvent,
  newTimes: EventTimes
): (ScheduleImpactCardProps | null)[] => {
  const slackReminders = event.slackReminders || [];

  return slackReminders
    .filter((reminder) => reminder.notifyStatus === REMINDER_STATUS.SCHEDULED)
    .map((reminder): ScheduleImpactCardProps | null => {
      // Only validate reminders with relative schedules
      // Absolute time reminders won't change when event time changes
      if (
        !reminder.schedule ||
        typeof reminder.schedule.quantity !== 'number' ||
        !reminder.schedule.unit ||
        reminder.schedule.beforeEventBegins === undefined
      ) {
        return null;
      }

      const absoluteTime = calculateScheduleTime(
        newTimes.startDateTime,
        reminder.schedule.quantity,
        reminder.schedule.unit,
        reminder.schedule.beforeEventBegins
      );
      const relativeDescription = getRelativeTimeDescription(
        reminder.schedule.quantity,
        reminder.schedule.unit,
        reminder.schedule.beforeEventBegins
      );

      if (!absoluteTime) {
        return null;
      }

      // If the reminder time hasn't changed, no validation needed
      if (reminder.datetime && isSameMinute(absoluteTime, parseUTCDate(reminder.datetime))) {
        return null;
      }

      const isPast = isBefore(absoluteTime, new Date());
      const formattedTime = formatDateInTimezone(
        absoluteTime,
        newTimes.timezoneCode,
        `${PRETTY_DATE_TIME_TZ_FORMAT} z`
      );

      const description = isPast ? (
        <>
          Sent immediately{' '}
          <span className="tw-text-negative-foreground tw-line-through">
            Scheduled send: {relativeDescription}
          </span>
        </>
      ) : (
        `Updated scheduled send time: ${formattedTime} (${relativeDescription})`
      );

      return {
        icon: MessageSquareText,
        heading: `Reminder: ${reminder.title}`,
        description
      };
    });
};

/**
 * Validate Email reminders
 * Returns an array of validation cards for scheduled Email reminders
 */
const validateEmailReminders = (
  event: EditableEvent,
  newTimes: EventTimes
): (ScheduleImpactCardProps | null)[] => {
  const emailReminders = event.emailReminders || [];

  return emailReminders
    .filter((reminder) => reminder.notifyStatus === REMINDER_STATUS.SCHEDULED)
    .map((reminder): ScheduleImpactCardProps | null => {
      // Only validate reminders with relative schedules
      // Absolute time reminders won't change when event time changes
      if (
        !reminder.schedule ||
        typeof reminder.schedule.quantity !== 'number' ||
        !reminder.schedule.unit ||
        reminder.schedule.beforeEventBegins === undefined
      ) {
        return null;
      }

      const absoluteTime = calculateScheduleTime(
        newTimes.startDateTime,
        reminder.schedule.quantity,
        reminder.schedule.unit,
        reminder.schedule.beforeEventBegins
      );
      const relativeDescription = getRelativeTimeDescription(
        reminder.schedule.quantity,
        reminder.schedule.unit,
        reminder.schedule.beforeEventBegins
      );

      if (!absoluteTime) {
        return null;
      }

      // If the reminder time hasn't changed, no validation needed
      if (reminder.datetime && isSameMinute(absoluteTime, parseUTCDate(reminder.datetime))) {
        return null;
      }

      const isPast = isBefore(absoluteTime, new Date());
      const formattedTime = formatDateInTimezone(
        absoluteTime,
        newTimes.timezoneCode,
        `${PRETTY_DATE_TIME_TZ_FORMAT} z`
      );

      const description = isPast ? (
        <>
          Sent immediately{' '}
          <span className="tw-text-negative-foreground tw-line-through">
            Scheduled send: {relativeDescription}
          </span>
        </>
      ) : (
        `Updated scheduled send time: ${formattedTime} (${relativeDescription})`
      );

      return {
        icon: MessageSquareText,
        heading: `Reminder: ${reminder.subject}`,
        description
      };
    });
};

/**
 * Validate check-in schedule
 */
const validateCheckinSchedule = (
  event: EditableEvent,
  newTimes: EventTimes
): ScheduleImpactCardProps | null => {
  if (!event.selfCheckinSchedule || !event.allowUserCheckin || !event.datetimeScheduled) {
    return null;
  }

  const { quantity, unit, beforeEventBegins } = event.selfCheckinSchedule;

  if (!quantity || !unit) {
    return null;
  }

  const originalCheckinDate = calculateScheduleTime(
    parseUTCDate(event.datetimeScheduled),
    quantity,
    unit,
    beforeEventBegins
  );

  // If the original check-in date is in the past, no validation needed
  // as it has already been sent
  if (isBefore(originalCheckinDate, new Date())) {
    return null;
  }

  const calculatedDate = calculateScheduleTime(
    newTimes.startDateTime,
    quantity,
    unit,
    beforeEventBegins
  );

  // If the check-in times haven't changed, no validation needed
  if (isSameMinute(originalCheckinDate, calculatedDate)) {
    return null;
  }

  const isPast = isBefore(calculatedDate, new Date());
  const formattedTime = formatDateInTimezone(
    calculatedDate,
    newTimes.timezoneCode,
    `${PRETTY_DATE_TIME_TZ_FORMAT} z`
  );
  const relativeDescription = getRelativeTimeDescription(quantity, unit, beforeEventBegins);

  const description = isPast ? (
    <>
      Sent immediately{' '}
      <span className="tw-text-negative-foreground tw-line-through">
        Scheduled send: {relativeDescription}
      </span>
    </>
  ) : (
    `Updated scheduled send time: ${formattedTime} (${relativeDescription})`
  );

  return {
    icon: CircleCheck,
    heading: 'Self check-in',
    description
  };
};

/**
 * Validate feedback form schedule
 * Only returns info if the primary feedback hasn't been sent yet
 */
const validateFeedbackSchedule = (
  event: EditableEvent,
  newTimes: EventTimes
): ScheduleImpactCardProps | null => {
  const { feedbackSchedule, feedbackEnable } = event;

  if (!feedbackEnable || !feedbackSchedule || !feedbackSchedule.sendAt) {
    return null;
  }

  // Check if feedback has already been sent by checking if sendAt is in the past
  const sendAtDate = parseUTCDate(feedbackSchedule.sendAt);
  if (isBefore(sendAtDate, new Date())) {
    return null;
  }

  let calculatedDate: Date;
  let isRelative = false;
  let relativeDescription: string | undefined;

  if (
    feedbackSchedule.quantity &&
    feedbackSchedule.unit &&
    feedbackSchedule.beforeEventEnds !== undefined
  ) {
    // Relative time based on event end
    isRelative = true;
    calculatedDate = calculateScheduleTime(
      newTimes.endDateTime,
      feedbackSchedule.quantity as number,
      feedbackSchedule.unit,
      feedbackSchedule.beforeEventEnds
    );
    relativeDescription = getRelativeTimeDescription(
      feedbackSchedule.quantity as number,
      feedbackSchedule.unit,
      null,
      feedbackSchedule.beforeEventEnds
    );
  } else {
    // Absolute time
    calculatedDate = sendAtDate;
  }

  // If the feedback time hasn't changed, no validation needed
  if (isSameMinute(calculatedDate, sendAtDate)) {
    return null;
  }

  const isPast = isBefore(calculatedDate, new Date());
  const formattedTime = formatDateInTimezone(
    calculatedDate,
    newTimes.timezoneCode,
    `${PRETTY_DATE_TIME_TZ_FORMAT} z`
  );

  const description = isPast ? (
    <>
      Sent immediately{' '}
      <span className="tw-text-negative-foreground tw-line-through">
        Scheduled send: {relativeDescription}
      </span>
    </>
  ) : (
    `Updated scheduled send: ${formattedTime}${
      isRelative && relativeDescription ? ` (${relativeDescription})` : ''
    }`
  );

  return {
    icon: Smile,
    heading: 'Feedback form',
    description
  };
};

/**
 * Generic cutoff validator
 * Cutoffs can't be after event ends
 * Returns description string or null
 */
const validateCutoff = (
  cutoffDate: string | null | undefined,
  newTimes: EventTimes
): string | null => {
  if (!cutoffDate) {
    return null;
  }

  const originalCutoff = parseUTCDate(cutoffDate);
  let newCutoff = originalCutoff;

  // If cutoff is after new event end time, adjust it to event end time
  if (isAfter(originalCutoff, newTimes.endDateTime)) {
    newCutoff = newTimes.endDateTime;
  }

  // Only show if the time actually changed
  if (newCutoff.getTime() === originalCutoff.getTime()) {
    return null;
  }

  const formattedTime = formatDateInTimezone(
    newCutoff,
    newTimes.timezoneCode,
    `${PRETTY_DATE_TIME_TZ_FORMAT} z`
  );

  return `Updated cutoff time: ${formattedTime}`;
};

/**
 * Validate registration cutoff
 * Registration cutoff can't be after event ends
 */
const validateRegistrationCutoff = (
  event: EditableEvent,
  newTimes: EventTimes
): ScheduleImpactCardProps | null => {
  const description = validateCutoff(event.registrationCutoffAt, newTimes);

  if (!description) {
    return null;
  }

  return {
    icon: Ticket,
    heading: 'Registration cutoff',
    description
  };
};

/**
 * Validate questionnaire cutoff
 * Questionnaire cutoff can't be after event ends
 */
const validateQuestionnaireCutoff = (
  event: EditableEvent,
  newTimes: EventTimes
): ScheduleImpactCardProps | null => {
  const description = validateCutoff(event.questionnaireCutoffAt, newTimes);

  if (!description) {
    return null;
  }

  return {
    icon: HelpCircle,
    heading: 'Questionnaire cutoff',
    description
  };
};

/**
 * Hook to get time change validations for single events
 * Returns an array of validation cards to display
 *
 * @param newStartDateTime - The new start date/time
 * @param newEndDateTime - The new end date/time
 * @param newTimezoneCode - The timezone code
 *
 * Uses the input times directly as new event times.
 */
export const useEventTimeChangeValidations = (
  newStartDateTime: Date | undefined,
  newEndDateTime: Date | undefined,
  newTimezoneCode: string | undefined
): ValidationResult => {
  const event = useEditableEvent();

  return useMemo(() => {
    if (
      !newStartDateTime ||
      !newEndDateTime ||
      !newTimezoneCode ||
      !event.datetimeScheduled ||
      !event.datetimeEnd
    ) {
      return [];
    }

    // Convert datetime values to UTC while keeping wallclock time the same
    const startDateTimeUTC = convertSendAtToDBValue(newStartDateTime, newTimezoneCode);
    const endDateTimeUTC = convertSendAtToDBValue(newEndDateTime, newTimezoneCode);

    const newStartDate = parseUTCDate(startDateTimeUTC);
    const newEndDate = parseUTCDate(endDateTimeUTC);
    const originalStartDate = parseUTCDate(event.datetimeScheduled);
    const originalEndDate = parseUTCDate(event.datetimeEnd);

    // For single events: use input times directly
    const newEventStartTime = newStartDate;
    const newEventEndTime = newEndDate;

    // If event times haven't changed, no validation needed
    if (
      isSameMinute(newEventStartTime, originalStartDate) &&
      isSameMinute(newEventEndTime, originalEndDate)
    ) {
      return [];
    }

    const newTimes: EventTimes = {
      startDateTime: newEventStartTime,
      endDateTime: newEventEndTime,
      timezoneCode: newTimezoneCode
    };

    const result: ValidationResult = [];

    // Collect all validations
    result.push(
      ...validateSlackReminders(event, newTimes),
      ...validateEmailReminders(event, newTimes),
      validateCheckinSchedule(event, newTimes),
      validateFeedbackSchedule(event, newTimes),
      validateRegistrationCutoff(event, newTimes),
      validateQuestionnaireCutoff(event, newTimes)
    );

    return result.filter((item) => item !== null);
  }, [event, newStartDateTime, newEndDateTime, newTimezoneCode]);
};

/**
 * Hook to get session time change validations
 * Returns an array of validation cards to display for border session time changes
 *
 * Handles both existing sessions (with sessionId) and draft/new sessions (without sessionId)
 * Uses the parent event's timezone since sessions inherit timezone from the event.
 */
export const useSessionTimeChangeValidations = (
  sessionId: number | undefined,
  newStartDateTime: Date | undefined,
  newEndDateTime: Date | undefined
): ValidationResult => {
  const event = useEditableEvent();
  const eventTimezoneCode = event.timezoneCode;

  const sessionsQuery = useSessionsQuery(event);

  return useMemo(() => {
    if (
      !newStartDateTime ||
      !newEndDateTime ||
      !eventTimezoneCode ||
      !event.datetimeScheduled ||
      !event.datetimeEnd
    ) {
      return [];
    }

    // Convert datetime values to UTC while keeping wallclock time the same
    const startDateTimeUTC = convertSendAtToDBValue(newStartDateTime, eventTimezoneCode);
    const endDateTimeUTC = convertSendAtToDBValue(newEndDateTime, eventTimezoneCode);

    const newStartDate = parseUTCDate(startDateTimeUTC);
    const newEndDate = parseUTCDate(endDateTimeUTC);
    const originalStartDate = parseUTCDate(event.datetimeScheduled);
    const originalEndDate = parseUTCDate(event.datetimeEnd);

    let newEventStartTime: Date;
    let newEventEndTime: Date;

    // If sessionId is provided, this is an existing session
    if (sessionId && sessionsQuery.data && sessionsQuery.data.length > 0) {
      const sessions = sessionsQuery.data;

      // Clone sessions and update the edited session with new times
      const updatedSessions: EditableSession[] = sessions.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              datetimeScheduled: startDateTimeUTC,
              datetimeEnd: endDateTimeUTC,
              timezoneCode: eventTimezoneCode
            }
          : s
      );

      // Calculate new border times from updated sessions
      const { startSession: updatedStartSession, endSession: updatedEndSession } =
        getBorderSessions(updatedSessions);

      if (!updatedStartSession || !updatedEndSession) {
        return [];
      }

      newEventStartTime = parseUTCDate(updatedStartSession.datetimeScheduled!);
      newEventEndTime = parseUTCDate(updatedEndSession.datetimeEnd!);
    } else {
      // For new/draft sessions without sessionId, calculate new event boundaries
      // by comparing with current boundaries (similar to old useEventTimeChangeValidations)

      // New event start time is the minimum of session start time and current event start time
      newEventStartTime =
        newStartDate.getTime() < originalStartDate.getTime() ? newStartDate : originalStartDate;

      // New event end time is the maximum of session end time and current event end time
      newEventEndTime =
        newEndDate.getTime() > originalEndDate.getTime() ? newEndDate : originalEndDate;
    }

    // If border times haven't changed, no validation needed
    if (
      isSameMinute(newEventStartTime, originalStartDate) &&
      isSameMinute(newEventEndTime, originalEndDate)
    ) {
      return [];
    }

    const newTimes: EventTimes = {
      startDateTime: newEventStartTime,
      endDateTime: newEventEndTime,
      timezoneCode: eventTimezoneCode
    };

    const result: ValidationResult = [];

    // Collect all validations using the new border times
    result.push(
      ...validateSlackReminders(event, newTimes),
      ...validateEmailReminders(event, newTimes),
      validateCheckinSchedule(event, newTimes),
      validateFeedbackSchedule(event, newTimes),
      validateRegistrationCutoff(event, newTimes),
      validateQuestionnaireCutoff(event, newTimes)
    );

    return result.filter((item) => item !== null);
  }, [event, eventTimezoneCode, sessionId, newStartDateTime, newEndDateTime, sessionsQuery.data]);
};
